import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Image,
  StyleSheet,
  Alert,
  ScrollView,
  PermissionsAndroid,
  Platform,
  Keyboard,
  ActivityIndicator,
  KeyboardAvoidingView,
} from 'react-native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Audio } from 'expo-av';
import * as FileSystem from 'expo-file-system';
import Voice from '@react-native-voice/voice';
import { RootStackParamList } from './App.web';
import { RouteProp, useIsFocused } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { apiUrl } from './utils/config';
import { useStory } from './StoryContext';
import * as Font from 'expo-font';

type NewCharacterRouteProp = RouteProp<RootStackParamList, 'NewCharacterPage'>;

type NewCharacterNavigationProp = StackNavigationProp<
  RootStackParamList,
  'NewCharacterPage'
>;

const NewCharacterPage: React.FC<{
  route: NewCharacterRouteProp;
  navigation: NewCharacterNavigationProp;
}> = ({ route, navigation }) => {
  const { selectedStory, sessionId, withImages } = route.params;

  // Helper function to validate parameters
  const areParamsValid = () => {
    return (
      selectedStory &&
      selectedStory.first_prompt
    );
  };

  const loadFonts = async () => {
        await Font.loadAsync({
          'Futura': require('./assets/fonts/futura.ttf'),
        });
      };

  // Redirect on refresh if parameters are invalid
  useEffect(() => {
    if (!areParamsValid()) {
      console.warn('Invalid parameters detected. Redirecting to ExistingCharactersPage.');
      navigation.replace('ExistingCharactersPage', {
        selectedStory: selectedStory || {
          story_id: 'fallback-id',
          title: 'Fallback Title',
          description: 'Fallback Description',
          image_url: '',
          chapters: 0,
          prompt_number: 0,
          category: 'Fallback Category',
          status: 'Fallback Status',
          image_key: 'fallback-image-key',
          first_prompt: 'Fallback Prompt',
          images: false,
        },
        withImages: withImages ?? false,
        sessionId: sessionId || 'fallback-session-id',
      });
    }
    loadFonts();
  }, [selectedStory, sessionId, withImages, navigation]);

  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [nameResponse] = useState('');
  const [voice, setVoice] = useState('Matthew');
  const [volume, setVolume] = useState(50);
  const [speed, setSpeed] = useState(1);
  const [activeRecognizer, setActiveRecognizer] = useState(false);
  const [audioFileUri, setAudioFileUri] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [characterStyle, setCharacterStyle] = useState('');
  const [characterTalent, setCharacterTalent] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [eyeColor, setEyeColor] = useState('');
  const [gender, setGender] = useState('');
  const [accessories, setAccessories] = useState('');
  const [customizeExpanded, setCustomizeExpanded] = useState(false);
  const [styleDropdownVisible, setStyleDropdownVisible] = useState(false);
  const [talentDropdownVisible, setTalentDropdownVisible] = useState(false);
  const [genderDropdownVisible, setGenderDropdownVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const isFocused = useIsFocused();
  const { setContext } = useStory();
  const [webAudioInstance, setWebAudioInstance] = useState<HTMLAudioElement | null>(null);
  const [characterImageKey, setCharacterImageKey] = useState<string | null>(null);
  const [flagModalVisible, setFlagModalVisible] = useState(false);
  const [flagType, setFlagType] = useState<'image' | 'text' | 'both' | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [characterJobId, setCharacterJobId] = useState<string | null>(null);


  const isNextDisabled =
  !name.trim() ||     // Name must not be empty
  !gender ||          // Must pick a gender
  !characterStyle ||  // Must pick a style
  !characterTalent ||   // Must pick a talent
  !eyeColor ||
  !hairColor ||
  !accessories;


  const handleToggleDropdown = (dropdown: string) => {
    setGenderDropdownVisible(dropdown === 'gender' ? !genderDropdownVisible : false);
    setStyleDropdownVisible(dropdown === 'style' ? !styleDropdownVisible : false);
    setTalentDropdownVisible(dropdown === 'talent' ? !talentDropdownVisible : false);
    setCustomizeExpanded(dropdown === 'customize' ? !customizeExpanded : false);
  };

  const handleInputFocus = () => {
    setStyleDropdownVisible(false);
    setTalentDropdownVisible(false);
    setCustomizeExpanded(false);
  };

  const audioRef = useRef<Audio.Sound | null>(null);
  const typedTextRef = useRef<Text>(null);
  const isNextClickedRef = useRef(false);
  const intervalRef = useRef<any>(null);
  let currentFieldSetter = useRef<((value: string) => void) | null>(null);

  const gendersArray = [
    'Boy',
    'Girl',
  ];

  const stylesArray = [
    'Adventurer',
    'Dragon Warrior',
    'Furry Explorer',
    'Magic Horned Friend',
    'Mighty Miner',
  ];

  const talentsArray = [
    'Music Maker',
    'Sneaky Explorer',
    'Healing Helper',
    'Brave Knight',
    'Magic User',
  ];

  const hairColorMap: Record<string, string> = {
    '#AA8866': 'Light Brown',
    '#DFBD99': 'Blonde',
    '#000000': 'Black',
    '#501901': 'Dark Brown',
    '#9A3202': 'Red',
    '#FFFFFF': 'White',
  };
  
  const eyeColorMap: Record<string, string> = {
    '#624E35': 'brown',
    '#2E536F': 'blue',
    '#3C671E': 'dark green',
    '#187847': 'light green',
    '#487664': 'teal',
    '#FFFFFF': 'white',
  };
  

  const hairColors = ['#AA8866', '#DFBD99', '#000000', '#501901', '#9A3202', '#FFFFFF'];
  const eyeColors = ['#624E35', '#2E536F', '#3C671E', '#187847', '#487664', '#FFFFFF']; 

  // Inside the onPress handlers for the color options
  const handleHairColorSelect = (color: string) => {
    setHairColor(hairColorMap[color]);
  };
  
  const handleEyeColorSelect = (color: string) => {
    setEyeColor(eyeColorMap[color]);
  };

  
  useEffect(() => {
    const init = async () => {
      const fetchedUserId = await getUserID();
      setUserId(fetchedUserId);
    init();
    }
  }, []);

  const getUserID = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const user_id = session?.tokens?.accessToken?.payload?.sub;
      return user_id || null;
    } catch (error) {
      console.error('Error retrieving user ID:', error);
      return null;
    }
  };

  const fetchUserSettings = useCallback(async (): Promise<{ voice: string; volume: number; speed: number }> => {
      try {
        const authToken = await fetchAuthSession().then((session) => session?.tokens?.idToken || null);
        const userID = await fetchAuthSession().then((session) => session?.tokens?.accessToken?.payload?.sub || null);
    
        if (!authToken || !userID) {
          throw new Error('Missing auth token or user ID');
        }
    
        const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${userID}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
    
        if (!response.ok) {
          throw new Error('Failed to fetch user settings');
        }
    
        const settings = await response.json();
        
        setVoice(settings.voice);
        setVolume(settings.volume);
        setSpeed(settings.speed);
        // Return the fetched settings
        return {
          voice: settings.voice || 'Matthew',
          volume: settings.volume,
          speed: settings.speed || 1,
        };
      } catch (error) {
        console.error('Error fetching user settings:', error);
        Alert.alert('Error', 'Failed to fetch user settings. Using defaults.');
    
        // Return default settings in case of error
        return {
          voice: 'Matthew',
          volume: 50,
          speed: 1,
        };
      }
    }, []);


  const saveBase64AudioToFile = async (base64Audio: string) => {
      try {
        if (Platform.OS === 'web') {
          playAudioWeb(base64Audio);
        } else {
          // Mobile: Save to filesystem
          const uri = `${FileSystem.cacheDirectory}story-audio.wav`;
          await FileSystem.writeAsStringAsync(uri, base64Audio, {
            encoding: FileSystem.EncodingType.Base64,
          });
          playAudio(uri); // Play audio after saving
        }
      } catch (error) {
        console.error('Error saving audio file:', error);
        Alert.alert('Error', 'Failed to save audio file.');
      }
    };
    
    const setAudioMode = async () => {
      try {
        await Audio.setAudioModeAsync({
          allowsRecordingIOS: false,
          staysActiveInBackground: true,
          playsInSilentModeIOS: true, // Allow audio playback even when the device is on silent
        });
      } catch (error) {
        console.error('Error setting audio mode:', error);
      }
    };
  
    const playAudio = async (uri: string) => {
      try {
        if (Platform.OS === 'ios'){
          setAudioMode();
        }
        let settingsVolume = 0; // Default to 0 for muted
        let speed = 1; // Default playback speed
        const settings = await fetchUserSettings();
        speed = settings.speed;
    
        if (!isMuted) {
          settingsVolume = settings.volume / 100; // Convert to 0-1 range
        }
    
        const { sound } = await Audio.Sound.createAsync({ uri });
        audioRef.current = sound;
        // Apply volume and playback speed
        await sound.setVolumeAsync(isMuted ? 0 : settingsVolume);
        await sound.setRateAsync(speed, true);
    
        await sound.playAsync();
        sound.setOnPlaybackStatusUpdate((status) => {
          if (status.isLoaded && status.didJustFinish) {
            console.log('Audio finished playing.');
          }
        });
      } catch (error) {
        console.error('Error playing audio:', error);
        Alert.alert('Error', 'Failed to play audio.');
      }
    };
  
    const playAudioWeb = async (base64Audio: string) => {
      try {
        console.log('playing audio')
        const settings = await fetchUserSettings();
    
        let settingsVolume = isMuted ? 0 : settings.volume / 100;
        let speed = settings.speed || 1;
    
        const newAudio = new window.Audio(`data:audio/wav;base64,${base64Audio}`);
    
        newAudio.volume = settingsVolume;
        newAudio.playbackRate = speed;
        newAudio.onended = () => {
          console.log('ending');
          setWebAudioInstance(null); // Reset instance when done
        };
    
        newAudio.play();
        setWebAudioInstance(newAudio); // Store the playing audio in state
    
      } catch (error) {
        console.error('Error playing audio on Web:', error);
        Alert.alert('Error', 'Failed to play audio on Web.');
      }
    };
     
  
    const stopAudio = async () => {
      if (Platform.OS === 'web') {
        if (webAudioInstance) {
          webAudioInstance.pause();
          webAudioInstance.currentTime = 0;
          setWebAudioInstance(null);
        }
      } else {
        if (audioRef.current) {
          try {
            const status = await audioRef.current.getStatusAsync();
            if (status.isLoaded) {
              await audioRef.current.stopAsync();
              await audioRef.current.unloadAsync();
            }
          } catch (error) {
            console.error('Error stopping or unloading mobile audio:', error);
          } finally {
            audioRef.current = null;
          }
        }
      }
    };
    
  
    useEffect(() => {
      if (!isFocused) {
        stopAudio(); // Stop audio when the page is not focused
      }
    }, [isFocused]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    // Updated characterInfo to include new fields
    const settings = await fetchUserSettings();

    const characterInfo = { 
      name,
      gender, 
      style: characterStyle, 
      talent: characterTalent, 
      hair_color: hairColor, 
      eye_color: eyeColor, 
      accessories, 
      voice: settings.voice  // ✅ Use directly from fresh settings
    };

    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}characters/submit-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(characterInfo),
      });

      if (!response.ok) throw new Error('Failed to submit job.');

      const data = await response.json();
      setAiResponse(data.ai_response);

      if (data.audio) await saveBase64AudioToFile(data.audio);

      // ✅ Store the real job_id (not just image key)
      setCharacterJobId(data.job_id);

      console.log('Starting polling with job_id:', data.job_id);
      startPolling(data.job_id, authToken);

    } catch (error) {
      console.error('Error submitting job:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const isChromeBrowser = () => {
    if (Platform.OS !== 'web') return false;
  
    const ua = navigator.userAgent;
    const isBrave = (navigator as any).brave?.isBrave?.() ?? ua.includes('Brave');
  
    return /Chrome/.test(ua) && !/Edg|OPR|Brave/.test(ua) && !isBrave;
  };
   

  const requestPermissions = async () => {
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
          {
            title: 'Microphone Permission',
            message: 'This app needs access to your microphone for speech-to-text.',
            buttonPositive: 'OK',
          }
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      } catch (err) {
        console.warn(err);
        return false;
      }
    } else if (Platform.OS === 'web') {
      console.log('Microphone permission is not required on the web.');
      return true;
    } else if (Platform.OS === 'ios') {
      const { status } = await Audio.requestPermissionsAsync(); // 👈 use expo-av for iOS
      return status === 'granted';
    }
    
  };

  const startListening = async (setter: (value: string) => void) => {
  // Always clean up listeners before starting
  await Voice.stop();
  await Voice.destroy();
  Voice.removeAllListeners();

  // Assign new listeners for this field only
  Voice.onSpeechResults = (event) => {
    if (event.value && event.value.length > 0) {
      setter(event.value[0]);
    }
    setActiveRecognizer(false);
    Voice.stop();
  };
  Voice.onSpeechEnd = () => {
    setActiveRecognizer(false);
    Voice.stop();
  };
  Voice.onSpeechError = (error) => {
    if (error?.error?.code === '11') {
      // Soft error, ignore
      return;
    }
    console.error('Speech recognition error:', error);
    Alert.alert('Error', 'Please try speaking more clearly.');
    setActiveRecognizer(false);
  };

  if (Platform.OS === 'web') {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      Alert.alert('Error', 'Your browser does not support speech recognition.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      setter(event.results[0][0].transcript);
    };

    recognition.onerror = (event: any) => {
      console.error('Web Speech error:', event.error);
    };

    recognition.start();
    return;
  }

  // Android/iOS permission logic
  const granted = await requestPermissions();
  if (!granted) return;

  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    try {
      await stopAudio();
      setActiveRecognizer(true);
      await Voice.start('en-US');
      setTimeout(() => {
        Voice.stop();
        setActiveRecognizer(false);
      }, 5000); // timeout safeguard
    } catch (err) {
      setActiveRecognizer(false);
      Alert.alert('Error', 'Failed to start speech recognition.');
    }
  }
};

  

  useEffect(() => {
  // No listeners here. Set up and tear down on-demand.
  return () => {
    Voice.stop();
    Voice.destroy().then(Voice.removeAllListeners);
  };
}, []);

  const startPolling = (job_id: string, authToken: string) => {
    intervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${apiUrl}characters/check-image-status?job_id=${job_id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        });

        const data = await response.json();

        if (data.status === 'COMPLETED') {
          clearInterval(intervalRef.current);
          setImageUrl(data.image_url);
          const imageKey = data.image_url?.split('?')[0].split('/').pop();
          setCharacterImageKey(imageKey);
        } else if (data.status === 'FAILED') {
          clearInterval(intervalRef.current);
          setError('Job failed. Please try again.');
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        clearInterval(intervalRef.current);
        setError('Error checking job status.');
      }
    }, 5000);
  };

  const handleNext = async () => {
    if (aiResponse && !isNextClickedRef.current) {
      isNextClickedRef.current = true;
      setLoading(true);

      try {
        const authToken = await getAuthToken();
        if (!authToken) throw new Error('Unable to retrieve auth token');

        const response = await fetch(`${apiUrl}story/model-response`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            first_prompt: selectedStory?.first_prompt,
            is_first_message: true,
            session_id: sessionId,
            character_description: aiResponse,
            talent: characterTalent,
            name: name,
            voice,
            prompt_number: 1,
            images: withImages, // Include the `withImages` value
            gender: gender,
            accessories,
            story_id: selectedStory?.story_id,
            character_image_key: characterImageKey,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setContext({
            sessionId: data.session_id,
            storyData: {
              story_id: selectedStory?.story_id ?? '', // Default to an empty string if undefined
              title: selectedStory?.title ?? 'Untitled', // Default to "Untitled"
              description: selectedStory?.description ?? '', // Default to an empty string
              image_url: selectedStory?.image_url ?? '', // Default to an empty string
              chapters: selectedStory?.chapters ?? 0, // Default to 0 chapters
              prompt_number: selectedStory?.prompt_number ?? 1, // Default to 1
              character_selected: selectedStory?.character_selected ?? false, // Default to false
              category: selectedStory?.category ?? 'General', // Default to "General"
              tags: selectedStory?.tags ?? [], // Default to an empty array
              status: selectedStory?.status ?? 'New', // Default to "New"
              session_id: selectedStory?.session_id ?? '', // Default to an empty string
              image_key: selectedStory?.image_key ?? '', // Default to an empty string
              first_prompt: selectedStory?.first_prompt ?? '', // Default to an empty string
              images: withImages ?? false, // Ensure `images` is a boolean
            },
            characterDescription: aiResponse || '',
            withImages, // Pass the `withImages` value
            initialResponse: {
              response: data.response,
              audio: data.audio,
              session_id: sessionId || '',
            },
          });
          navigation.navigate('StoryPage', {
            sessionId: data.session_id,
            storyData: {
              story_id: selectedStory?.story_id ?? '', // Default to an empty string if undefined
              title: selectedStory?.title ?? 'Untitled', // Default to "Untitled"
              description: selectedStory?.description ?? '', // Default to an empty string
              image_url: selectedStory?.image_url ?? '', // Default to an empty string
              chapters: selectedStory?.chapters ?? 0, // Default to 0 chapters
              prompt_number: selectedStory?.prompt_number ?? 1, // Default to 1
              character_selected: selectedStory?.character_selected ?? false, // Default to false
              category: selectedStory?.category ?? 'General', // Default to "General"
              tags: selectedStory?.tags ?? [], // Default to an empty array
              status: selectedStory?.status ?? 'New', // Default to "New"
              session_id: selectedStory?.session_id ?? '', // Default to an empty string
              image_key: selectedStory?.image_key ?? '', // Default to an empty string
              first_prompt: selectedStory?.first_prompt ?? '', // Default to an empty string
              images: withImages ?? false, // Ensure `images` is a boolean
            },
            characterDescription: aiResponse || '',
            withImages: withImages ?? false, // Ensure `withImages` is a boolean
            initialResponse: {
              response: data.response,
              audio: data.audio,
              session_id: sessionId || '',
            },
          });
          
          
        }
      } catch (error) {
        console.error('Error starting story:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMute = async () => {
      if (Platform.OS === 'web') {
        try {
          if (webAudioInstance) {
            let newVolume;
            if (isMuted){
              newVolume = volume > 0 ? volume / 100 : 1/2;
            } else {
              newVolume = 0;
            };
            updateSettings(newVolume * 100);
            webAudioInstance.volume = newVolume;
            console.log('New volume: ', newVolume)
            console.log('webInstance volume: ', webAudioInstance.volume)
            setIsMuted(!isMuted);
          } else {
            console.warn('No active web audio instance found.');
          }
        } catch (error) {
          console.error('Error toggling mute on web:', error);
        }
      } else {
        try {
          if (audioRef.current) {
            let newVolume;
            if (isMuted){
              newVolume = volume > 0 ? volume / 100 : 1/2;
            } else {
              newVolume = 0;
            };
            updateSettings(newVolume * 100);
            await audioRef.current.setVolumeAsync(newVolume);
            setIsMuted(!isMuted);
          } else {
            console.warn('No audio instance to mute/unmute.');
          }
        } catch (error) {
          console.error('Error toggling mute:', error);
        }
      }
    };  
    
    const updateSettings = async (volume: number) => {
      try {
        const user_id = await getUserID();
        const authToken = await getAuthToken();
        const response = await fetch(`${apiUrl}settings/set-user-settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            user_id,
            volume,
          }),
        });
        console.log('Settings updated successfully:', await response.json());
      } catch (error) {
        console.error('Error updating settings:', error);
      }
    };

  const handleBack = () => {
    // Clear the context when navigating back
    setContext(null);
    navigation.navigate('StoriesPage'); // Navigate to StoriesPage explicitly
  };

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const sendFlag = async ({
    message,
    flagType = 'flag',
  }: {
    message: string;
    flagType?: 'flag' | 'bug' | 'feedback';
  }) => {
    try {
      const response = await fetch(`${apiUrl}user/contact-us`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: flagType,
          message,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Error submitting flag:', result.error);
        Alert.alert('Error', 'Something went wrong. Please try again.');
        return;
      }

      Alert.alert('Thank you', 'Your report has been submitted.');
    } catch (err) {
      console.error('Flag send failed:', err);
      Alert.alert('Error', 'An error occurred. Please try again later.');
    }
  };

  const handleFlag = () => {
    setFlagModalVisible(true);
  };

  const submitFlag = async () => {
    if (!flagType || !flagReason.trim()) {
      Alert.alert('Please select an issue type and provide a reason.');
      return;
    }

    const message = `CHARACTER FLAG\nName: ${name}\nReason:\n${flagReason}`;

    await sendFlag({ message, flagType: 'flag' });

    setFlagModalVisible(false);
    setFlagType(null);
    setFlagReason('');

    // 🔥 Delete character
    try {
      const authToken = await getAuthToken();
      if (!authToken) {
        Alert.alert('Error', 'Unable to retrieve auth token');
        return;
      }

      const response = await fetch(`${apiUrl}characters/delete-character`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          job_id: characterJobId,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        Alert.alert('Error', `Failed to delete character: ${errorData.message}`);
        return;
      }

      if (!selectedStory || !withImages || !sessionId) {
        navigation.navigate('StoriesPage');
        return;
      }

      navigation.replace('ExistingCharactersPage', {
        selectedStory,
        withImages,
        sessionId,
      });
    } catch (err) {
      console.error('Error deleting flagged character:', err);
      Alert.alert('Error', 'Something went wrong while deleting the character.');
    }
  };


  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: '#F7F8FB' }}
      // Try "padding" on both platforms first. If that doesn't work well on Android, switch to 'position' for Android.
      behavior={Platform.OS === 'ios' ? 'padding' : 'padding'}
      // Adjust the offset if you have a header or other top spacing
      keyboardVerticalOffset={Platform.OS === 'ios' ? 60 : 60}
    >
      {loading && (
        <View style={styles.loadingOverlay}>
          <Image source={require('./assets/wizard.gif')} style={styles.loadingGif} />
        </View>
      )}
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
        </TouchableOpacity>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          {aiResponse && imageUrl && (
            <TouchableOpacity onPress={handleFlag}>
              <Image source={require('./assets/flag-icon.png')} style={{ width: 24, height: 24 }} />
            </TouchableOpacity>
          )}
          {aiResponse && (
            <TouchableOpacity onPress={handleMute} style={styles.muteButton}>
              <Image
                source={
                  isMuted
                    ? require('./assets/unmute-btn.png')
                    : require('./assets/mute-btn.png')
                }
                style={styles.muteIcon}
              />
            </TouchableOpacity>
          )}
        </View>
      </View>
      <ScrollView
        // Make sure content can stretch
        contentContainerStyle={{ flexGrow: 1 }}
        // This ensures taps on TextInput aren't swallowed
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.container}>
  
          <TouchableWithoutFeedback
            onPress={Platform.OS !== 'web' ? Keyboard.dismiss : undefined}
            accessible={false}
            style={{ flex: 1 }}
          >
            <View style={styles.centeredContent}>
              {!aiResponse ? (
                <>
                  <Text style={styles.title}>Tell Me About Your Character...</Text>
                  <View style={styles.inputGroup}>
                    <TextInput
                      style={styles.input}
                      placeholder="What is your name?"
                      placeholderTextColor="#6C6C6C"
                      value={name}
                      onChangeText={setName}
                      onFocus={handleInputFocus}
                    />
                    {(Platform.OS === 'ios') || (Platform.OS === 'web' && isChromeBrowser()) && (
                      <TouchableOpacity style={styles.micButton} onPress={() => startListening(setName)}>
                        <Text>🎤</Text>
                      </TouchableOpacity>
                    )}
                  </View>

                  {/* Character Gender Dropdown */}
                  <View style={styles.dropdownContainer}>
                    <TouchableOpacity
                      style={styles.dropdown}
                      onPress={() => handleToggleDropdown('gender')}
                    >
                      <Text
                        style={{
                          ...styles.dropdownText,
                          color: gender ? '#000' : '#6C6C6C',
                        }}
                      >
                        {gender || 'Pick Your Gender'}
                      </Text>
                      <Image
                        source={require('./assets/dropdown-icon.png')}
                        style={styles.dropdownIcon}
                      />
                    </TouchableOpacity>
                    {genderDropdownVisible &&
                      gendersArray.map((gender) => (
                        <TouchableOpacity
                          key={gender}
                          style={styles.dropdownOption}
                          onPress={() => {
                            setGender(gender);
                            setGenderDropdownVisible(false);
                          }}
                        >
                          <Text style={styles.dropdownOptionText}>{gender}</Text>
                        </TouchableOpacity>
                      ))}
                  </View>
  
                  {/* Character Style Dropdown */}
                  <View style={styles.dropdownContainer}>
                    <TouchableOpacity
                      style={styles.dropdown}
                      onPress={() => handleToggleDropdown('style')}
                    >
                      <Text
                        style={{
                          ...styles.dropdownText,
                          color: characterStyle ? '#000' : '#6C6C6C',
                        }}
                      >
                        {characterStyle || 'Pick a Character Style'}
                      </Text>
                      <Image
                        source={require('./assets/dropdown-icon.png')}
                        style={styles.dropdownIcon}
                      />
                    </TouchableOpacity>
                    {styleDropdownVisible &&
                      stylesArray.map((style) => (
                        <TouchableOpacity
                          key={style}
                          style={styles.dropdownOption}
                          onPress={() => {
                            setCharacterStyle(style);
                            setStyleDropdownVisible(false);
                          }}
                        >
                          <Text style={styles.dropdownOptionText}>{style}</Text>
                        </TouchableOpacity>
                      ))}
                  </View>
  
                  {/* Talent Dropdown */}
                  <View style={styles.dropdownContainer}>
                    <TouchableOpacity
                      style={styles.dropdown}
                      onPress={() => handleToggleDropdown('talent')}
                    >
                      <Text
                        style={{
                          ...styles.dropdownText,
                          color: characterTalent ? '#000' : '#6C6C6C',
                        }}
                      >
                        {characterTalent || 'Pick a Talent'}
                      </Text>
                      <Image
                        source={require('./assets/dropdown-icon.png')}
                        style={styles.dropdownIcon}
                      />
                    </TouchableOpacity>
                    {talentDropdownVisible &&
                      talentsArray.map((talent) => (
                        <TouchableOpacity
                          key={talent}
                          style={styles.dropdownOption}
                          onPress={() => {
                            setCharacterTalent(talent);
                            setTalentDropdownVisible(false);
                          }}
                        >
                          <Text style={styles.dropdownOptionText}>{talent}</Text>
                        </TouchableOpacity>
                      ))}
                  </View>
  
                  {/* Customize Look */}
                  <View style={styles.dropdownContainer}>
                    <TouchableOpacity
                      style={styles.dropdown}
                      onPress={() => handleToggleDropdown('customize')}
                    >
                      <Text
                        style={{
                          ...styles.dropdownText,
                          color: hairColor && eyeColor ? '#000' : '#6C6C6C',
                        }}
                      >
                        Customize Their Look
                      </Text>
                      <Image
                        source={require('./assets/dropdown-icon.png')}
                        style={styles.dropdownIcon}
                      />
                    </TouchableOpacity>
                    {customizeExpanded && (
                      <View style={styles.customizeContainer}>
                        <Text style={styles.centeredLabel}>Hair Color</Text>
                        <View style={styles.centeredColorOptions}>
                          {hairColors.map((color) => (
                            <TouchableOpacity
                              key={color}
                              style={[
                                styles.colorOption,
                                {
                                  backgroundColor: color,
                                  borderColor:
                                    hairColor === hairColorMap[color] ? '#5B8FCB' : '#000',
                                  marginHorizontal: 3,
                                },
                              ]}
                              onPress={() => handleHairColorSelect(color)}
                            />
                          ))}
                        </View>
                        <Text style={styles.centeredLabel}>Eye Color</Text>
                        <View style={styles.centeredColorOptions}>
                          {eyeColors.map((color) => (
                            <TouchableOpacity
                              key={color}
                              style={[
                                styles.colorOption,
                                {
                                  backgroundColor: color,
                                  borderColor:
                                    eyeColor === eyeColorMap[color] ? '#5B8FCB' : '#000',
                                  marginHorizontal: 3,
                                },
                              ]}
                              onPress={() => handleEyeColorSelect(color)}
                            />
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
  
                  {/* Accessories Input */}
                  <View style={styles.inputGroup}>
                    <TextInput
                      style={styles.input}
                      placeholder="Accessories"
                      placeholderTextColor="#6C6C6C"
                      value={accessories}
                      onChangeText={setAccessories}
                      onFocus={handleInputFocus}
                    />
                    {(Platform.OS === 'ios') || (Platform.OS === 'web' && isChromeBrowser()) && (
                      <TouchableOpacity style={styles.micButton} onPress={() => startListening(setAccessories)}>
                        <Text>🎤</Text>
                      </TouchableOpacity>
                    )}
                  </View>
  
                  {/* Next Button */}
                  <View style={styles.buttonContainer}>
                    <View style={styles.nextShadowBackground} />
                    <TouchableOpacity
                      onPress={handleSubmit}
                      disabled={isNextDisabled}
                      style={[
                        styles.nextButton,
                        isNextDisabled && { opacity: 0.5 }, // Dim the button if disabled
                      ]}
                    >
                      <Text style={styles.nextButtonText}>Next</Text>
                    </TouchableOpacity>
                  </View>
                </>
              ) : (
                <View style={styles.characterResult}>
                  <Text style={styles.characterName}>{nameResponse}</Text>
  
                  {imageUrl && imageUrl.trim() ? (
                    <Image
                      key={`${imageUrl}-${Date.now()}`}
                      source={{ uri: imageUrl }}
                      style={[styles.characterImage, { resizeMode: 'contain' }]}
                      onError={(e) =>
                        console.error('Image failed to load:', e.nativeEvent.error)
                      }
                      onLoad={() => console.log('Image loaded successfully:', imageUrl)}
                    />
                  ) : (
                    <View style={styles.placeholder}>
                      <ActivityIndicator size="large" color="#ccc" />
                    </View>
                  )}
  
                  <Text ref={typedTextRef} style={styles.characterDescription}>
                    {aiResponse}
                  </Text>
  
                  <View style={styles.buttonContainer}>
                    <View style={styles.nextShadowBackground} />
                    <TouchableOpacity onPress={handleNext} style={styles.nextButton}>
                      <Text style={styles.nextButtonText}>Next</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </TouchableWithoutFeedback>
        </View>
      </ScrollView>
      {flagModalVisible && (
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 20
        }}>
          <View style={{
            backgroundColor: 'white', padding: 20, borderRadius: 10, width: '90%', maxWidth: 400
          }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 10 }}>What seems to be the issue?</Text>

            {['image', 'text', 'both'].map((option) => (
              <TouchableOpacity
                key={option}
                onPress={() => setFlagType(option as 'image' | 'text' | 'both')}
                style={{
                  backgroundColor: flagType === option ? '#544564' : '#ccc',
                  padding: 10, borderRadius: 8, marginVertical: 5
                }}
              >
                <Text style={{ color: '#fff', textAlign: 'center' }}>{capitalize(option)}</Text>
              </TouchableOpacity>
            ))}

            <TextInput
              multiline
              numberOfLines={4}
              placeholder="Tell us why..."
              value={flagReason}
              onChangeText={setFlagReason}
              style={{
                marginTop: 15, borderColor: '#ccc', borderWidth: 1,
                borderRadius: 8, padding: 10, textAlignVertical: 'top'
              }}
            />

            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15 }}>
              <TouchableOpacity
                onPress={() => {
                  setFlagModalVisible(false);
                  setFlagType(null);
                  setFlagReason('');
                }}
              >
                <Text style={{
                  color: 'white',
                  backgroundColor: '#534466',
                  padding: 10,
                  borderRadius: 20
                }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={submitFlag}>
                <Text style={{
                  color: 'white',
                  backgroundColor: '#534466',
                  padding: 10,
                  borderRadius: 20
                }}>Submit</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}  

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#F7F8FB',
    alignItems: 'center',
    flexGrow: 1,
    width: '100%',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject, // Fills the entire screen
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent gray
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10, // Ensure it overlays other content
  },
  loadingGif: {
    width: 150,
    height: 150,
  },
  scrollContent: {
    backgroundColor: '#F7F8FB',
    minHeight: '100%',
  },
  centeredContent: {
    justifyContent: 'center', // Center vertically
    alignItems: 'center', // Center horizontally
    flex: 1,
    backgroundColor: '#F7F8FB',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: 60, // Pick a height that matches your design
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  backButton: { 
    marginRight: 10,
  },
  backIcon: { 
    width: 24, 
    height: 24 
  },
  flagIcon: {
    width: 24,
    height: 24,
  },
  muteButton: {
    marginRight: 10,
  },
  muteIcon: {
    width: 24,
    height: 24,
  },
  form: { 
    width: '100%', 
    alignItems: 'center', 
  },
  title: { 
    fontSize: 24, 
    marginBottom: 70,
    width: 230,
    fontFamily: 'Futura',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  inputGroup: { 
    flexDirection: 'row', 
    alignItems: 'center', 
    backgroundColor: '#D9D9D9', 
    marginBottom: 20,
    borderRadius: 20,
    paddingHorizontal: 20,
    width: '100%',
  },
  input: { 
    flex: 1, 
    padding: 20, 
    backgroundColor: '#D9D9D9', 
    fontFamily: 'Futura',
    fontSize: 18,
    textAlign: 'center',
    fontWeight: 'bold',
    ...Platform.select({
      web: {
        outlineStyle: 'none', // Remove the outline for web
      },
    }),
  },
  dropdownContainer: {
    width: '100%', // Match parent width
    marginBottom: 20,
    backgroundColor: '#D9D9D9',
    borderRadius: 20,
    paddingHorizontal: 20, // Matches input styling
  },
  dropdown: {
    flex: 1, // Matches the input's flexible width
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10, // Matches input padding
    paddingVertical: 20,
    backgroundColor: '#D9D9D9',
    borderRadius: 20,
    width: '100%', // Ensures the dropdown spans full width
  },
  dropdownText: {
    flex: 1, // Ensures the text spans available space
    fontFamily: 'Futura',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  dropdownIcon: {
    width: 20,
    height: 20,
    marginLeft: 10, // Proper spacing for the icon
  },  
  dropdownOption: {
    padding: 10,
    backgroundColor: '#D9D9D9',
    textAlign: 'center',
    borderRadius: 20,
  },
  dropdownOptionText: {
    fontFamily: 'Futura',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: 'bold',
    color: "#6C6C6C",
  },
  label: {
    marginBottom: 5,
    fontWeight: 'bold',
  },
  toggleButton: {
    marginTop: 10,
    padding: 10,
    backgroundColor: '#007BFF',
    borderRadius: 5,
  },
  toggleButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  centeredLabel: {
    fontWeight: 'bold',
    fontSize: 18,
    textAlign: 'center',
    marginVertical: 10,
    color: '#6C6C6C',
    fontFamily: 'Futura',
  },
  customizeContainer: {
    marginBottom: 20,
  },
  centeredColorOptions: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 10,
  },
  colorOption: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 3,
    borderColor: '#000',
  },
  button: {
    backgroundColor: '#007BFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  buttonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  micButton: { 
    marginLeft: 10, 
    backgroundColor: '#AFC9BF',
    padding: 10,
    borderRadius: 50,
  },
  error: { 
    color: 'red', 
    marginBottom: 10 
  },
  submitButton: { 
    backgroundColor: '#544564', 
    padding: 15, 
    paddingHorizontal: 100,
    borderRadius: 15, 
    alignItems: 'center', 
    alignSelf: 'center',
    width: 251,
    height: 55,
    marginTop: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  submitShadowBackground: {
    position: 'absolute',
    width: 251,
    height: 55,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '25%',
    left: '1%',
  },
  submitButtonText: { 
    color: '#FFF', 
    fontSize: 16 
  },
  characterResult: { 
    width: '95%', 
    alignItems: 'flex-start',
    marginVertical: 20,
  },
  characterName: { 
    fontSize: 24, 
    fontWeight: 'bold', 
    fontFamily: 'Futura', 
    textAlign: 'left',
    marginBottom: 10,
  },
  characterImage: {
    width: '100%', 
    maxWidth: 500,
    height: undefined, 
    aspectRatio: 1, 
    borderRadius: 15, 
    marginBottom: 20, 
    backgroundColor: '#E0E0E0',
    alignSelf: 'center',
  },
  placeholder: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 1,
    borderRadius: 15,
    marginBottom: 20,
    alignSelf: 'center', // Matches the image's alignment
    justifyContent: 'center', // Centers the spinner vertically
    alignItems: 'center', // Centers the spinner horizontally
    backgroundColor: '#f0f0f0', // Neutral background for the placeholder
  },
  characterDescription: { 
    fontSize: 16, 
    textAlign: 'left',
    fontFamily: 'Futura',
    maxWidth: 500,
  },
  buttonContainer: {
    alignSelf: 'center',
  },
  nextButton: { 
    backgroundColor: '#544564', 
    padding: 15, 
    paddingHorizontal: 100,
    borderRadius: 15, 
    alignItems: 'center', 
    alignSelf: 'center',
    width: 238,
    height: 55,
    marginTop: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  nextShadowBackground: {
    position: 'absolute',
    width: 238,
    height: 55,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    top: '25%',
    left: '1%',
  },
  nextButtonText: { 
    color: '#FFF', 
    fontSize: 16 
  },
  audioControls: {
    marginTop: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },

});


export default NewCharacterPage;
